/**
 * Cross-App Bonus System for OPMODE <-> GunfighterOS
 *
 * Calculates and awards bonuses for users who train in both apps.
 */

import { doc, updateDoc } from 'firebase/firestore';
import { db as firestore } from './firebase';
import {
  getLinkStatus,
  isDualTrainingBonusEligible,
  claimDualTrainingBonus,
  CrossAppActivity,
} from './crossAppSync';
import {
  loadProgressionState as loadProgressionStateFromDexie,
  saveProgressionState as saveProgressionStateToDexie,
} from './progressionService';
import type { DBProgressionState } from './db';

// ============================================================================
// BONUS DEFINITIONS
// ============================================================================

export interface CrossAppBonus {
  id: string;
  name: string;
  type: 'one-time' | 'weekly' | 'milestone';
  intelReward: number;
  description: string;
}

export const CROSS_APP_BONUSES: Record<string, CrossAppBonus> = {
  // One-time bonuses
  'link-bonus': {
    id: 'link-bonus',
    name: 'Cross-Training Pioneer',
    type: 'one-time',
    intelReward: 500,
    description: 'First-time account link between OPMODE and GunfighterOS',
  },

  // Weekly bonuses
  'dual-training-weekly': {
    id: 'dual-training-weekly',
    name: 'Dual Threat',
    type: 'weekly',
    intelReward: 0, // 10% bonus applied to session Intel instead
    description: 'Session in both apps during the same week',
  },

  // Milestone bonuses
  'consistency-4-weeks': {
    id: 'consistency-4-weeks',
    name: 'Dual Operator',
    type: 'milestone',
    intelReward: 1000,
    description: '4+ consecutive weeks of dual training',
  },
  'consistency-8-weeks': {
    id: 'consistency-8-weeks',
    name: 'Combat Ready',
    type: 'milestone',
    intelReward: 2000,
    description: '8+ consecutive weeks of dual training',
  },
  'consistency-12-weeks': {
    id: 'consistency-12-weeks',
    name: 'Tactical Athlete',
    type: 'milestone',
    intelReward: 3000,
    description: '12+ consecutive weeks of dual training',
  },

  // Same-day combo bonuses
  'combat-tested': {
    id: 'combat-tested',
    name: 'Combat Tested',
    type: 'weekly',
    intelReward: 50,
    description: 'GunfighterOS session on the same day as OPMODE workout',
  },
};

// ============================================================================
// BONUS CALCULATION
// ============================================================================

export interface BonusResult {
  bonusId: string;
  name: string;
  intelAwarded: number;
  claimed: boolean;
  message: string;
}

/**
 * Check and claim the one-time link bonus
 */
export async function checkLinkBonus(userId: string): Promise<BonusResult | null> {
  const status = await getLinkStatus(userId);

  if (!status.isLinked || !status.link) {
    return null;
  }

  // Already claimed?
  if (status.link.linkBonusClaimed) {
    return null;
  }

  // Award the bonus
  const bonus = CROSS_APP_BONUSES['link-bonus'];
  const state = await loadProgressionStateFromDexie(userId);

  state.lifetimeIntel += bonus.intelReward;
  state.weeklyIntel += bonus.intelReward;
  state.operation.intelEarned += bonus.intelReward;

  await saveProgressionStateToDexie(state);

  // Mark as claimed in Firestore
  await updateDoc(doc(firestore, 'crossAppLinks', userId), {
    linkBonusClaimed: true,
    linkBonusClaimedAt: new Date().toISOString(),
  });

  await updateDoc(doc(firestore, 'crossAppActivity', userId), {
    linkBonusClaimed: true,
  });

  console.log(` Link bonus claimed: +${bonus.intelReward} Intel`);

  return {
    bonusId: 'link-bonus',
    name: bonus.name,
    intelAwarded: bonus.intelReward,
    claimed: true,
    message: `+${bonus.intelReward} Intel for linking OPMODE with GunfighterOS!`,
  };
}

/**
 * Calculate the dual training bonus multiplier for a session
 * Returns a percentage multiplier (e.g., 10 for 10% bonus)
 */
export async function getDualTrainingMultiplier(userId: string): Promise<number> {
  const eligible = await isDualTrainingBonusEligible(userId);
  return eligible ? 10 : 0; // 10% bonus when dual training is active
}

/**
 * Check and claim consistency milestone bonuses
 */
export async function checkConsistencyBonuses(userId: string): Promise<BonusResult[]> {
  const status = await getLinkStatus(userId);
  if (!status.isLinked || !status.activity) {
    return [];
  }

  const results: BonusResult[] = [];
  const consecutiveWeeks = status.activity.consecutiveDualWeeks;
  const claimedMilestones = status.activity.consistencyBonusesClaimed || [];

  // Check each milestone
  const milestones = [
    { id: 'consistency-4-weeks', weeks: 4 },
    { id: 'consistency-8-weeks', weeks: 8 },
    { id: 'consistency-12-weeks', weeks: 12 },
  ];

  for (const milestone of milestones) {
    if (consecutiveWeeks >= milestone.weeks && !claimedMilestones.includes(milestone.id)) {
      const bonus = CROSS_APP_BONUSES[milestone.id];

      // Award Intel
      const state = await loadProgressionStateFromDexie(userId);
      state.lifetimeIntel += bonus.intelReward;
      state.weeklyIntel += bonus.intelReward;
      state.operation.intelEarned += bonus.intelReward;
      await saveProgressionStateToDexie(state);

      // Mark as claimed
      claimedMilestones.push(milestone.id);
      await updateDoc(doc(firestore, 'crossAppActivity', userId), {
        consistencyBonusesClaimed: claimedMilestones,
      });

      results.push({
        bonusId: milestone.id,
        name: bonus.name,
        intelAwarded: bonus.intelReward,
        claimed: true,
        message: `MILESTONE: ${bonus.name} - +${bonus.intelReward} Intel for ${milestone.weeks} weeks of dual training!`,
      });

      console.log(` ${bonus.name} milestone unlocked: +${bonus.intelReward} Intel`);
    }
  }

  return results;
}

/**
 * Check for same-day combo bonus (OPMODE + GunfighterOS on same day)
 */
export async function checkCombatTestedBonus(
  userId: string,
  sessionDate: Date = new Date()
): Promise<BonusResult | null> {
  const status = await getLinkStatus(userId);
  if (!status.isLinked || !status.activity) {
    return null;
  }

  const todayStr = sessionDate.toISOString().split('T')[0];

  // Check if GunfighterOS was used today
  if (status.activity.lastGunfighterSession !== todayStr) {
    return null;
  }

  // Already claimed for today via the lastOpmodeSession check
  if (status.activity.lastOpmodeSession === todayStr) {
    // Could track per-day combo claims if needed
    // For now, just award once when both sessions happen same day
  }

  const bonus = CROSS_APP_BONUSES['combat-tested'];
  const state = await loadProgressionStateFromDexie(userId);

  state.lifetimeIntel += bonus.intelReward;
  state.weeklyIntel += bonus.intelReward;
  state.operation.intelEarned += bonus.intelReward;

  await saveProgressionStateToDexie(state);

  console.log(` Combat Tested bonus: +${bonus.intelReward} Intel`);

  return {
    bonusId: 'combat-tested',
    name: bonus.name,
    intelAwarded: bonus.intelReward,
    claimed: true,
    message: `+${bonus.intelReward} Intel for training both physical and tactical today!`,
  };
}

/**
 * Apply all applicable cross-app bonuses for a session
 * Called during Intel award process
 */
export async function applySessionCrossAppBonuses(
  userId: string,
  baseIntel: number,
  sessionDate: Date = new Date()
): Promise<{
  bonusIntel: number;
  bonusMultiplier: number;
  bonuses: BonusResult[];
}> {
  const status = await getLinkStatus(userId);

  if (!status.isLinked) {
    return { bonusIntel: 0, bonusMultiplier: 0, bonuses: [] };
  }

  const bonuses: BonusResult[] = [];
  let bonusIntel = 0;

  // Check link bonus (one-time)
  const linkBonus = await checkLinkBonus(userId);
  if (linkBonus) {
    bonuses.push(linkBonus);
    bonusIntel += linkBonus.intelAwarded;
  }

  // Check dual training multiplier
  const multiplier = await getDualTrainingMultiplier(userId);
  if (multiplier > 0) {
    const multiplierBonus = Math.round(baseIntel * (multiplier / 100));
    bonusIntel += multiplierBonus;

    // Claim the weekly bonus
    await claimDualTrainingBonus(userId);

    bonuses.push({
      bonusId: 'dual-training-weekly',
      name: 'Dual Training Bonus',
      intelAwarded: multiplierBonus,
      claimed: true,
      message: `+${multiplier}% Intel bonus for dual training this week!`,
    });
  }

  // Check consistency milestones
  const milestones = await checkConsistencyBonuses(userId);
  bonuses.push(...milestones);
  bonusIntel += milestones.reduce((sum, m) => sum + m.intelAwarded, 0);

  // Check same-day combo bonus
  const comboBonus = await checkCombatTestedBonus(userId, sessionDate);
  if (comboBonus) {
    bonuses.push(comboBonus);
    bonusIntel += comboBonus.intelAwarded;
  }

  return {
    bonusIntel,
    bonusMultiplier: multiplier,
    bonuses,
  };
}

// ============================================================================
// BONUS STATUS & DISPLAY
// ============================================================================

export interface CrossAppBonusStatus {
  isLinked: boolean;
  linkBonus: {
    available: boolean;
    claimed: boolean;
    amount: number;
  };
  dualTraining: {
    eligible: boolean;
    multiplier: number;
    opmodeSessionsThisWeek: number;
    gunfighterSessionsThisWeek: number;
  };
  consistency: {
    consecutiveWeeks: number;
    nextMilestone: {
      name: string;
      weeksNeeded: number;
      reward: number;
    } | null;
  };
}

/**
 * Get current cross-app bonus status for UI display
 */
export async function getCrossAppBonusStatus(userId: string): Promise<CrossAppBonusStatus> {
  const status = await getLinkStatus(userId);

  if (!status.isLinked) {
    return {
      isLinked: false,
      linkBonus: { available: true, claimed: false, amount: 500 },
      dualTraining: {
        eligible: false,
        multiplier: 0,
        opmodeSessionsThisWeek: 0,
        gunfighterSessionsThisWeek: 0,
      },
      consistency: {
        consecutiveWeeks: 0,
        nextMilestone: {
          name: 'Dual Operator',
          weeksNeeded: 4,
          reward: 1000,
        },
      },
    };
  }

  const activity = status.activity!;
  const claimedMilestones = activity.consistencyBonusesClaimed || [];

  // Find next unclaimed milestone
  let nextMilestone: CrossAppBonusStatus['consistency']['nextMilestone'] = null;
  if (!claimedMilestones.includes('consistency-4-weeks')) {
    nextMilestone = { name: 'Dual Operator', weeksNeeded: 4, reward: 1000 };
  } else if (!claimedMilestones.includes('consistency-8-weeks')) {
    nextMilestone = { name: 'Combat Ready', weeksNeeded: 8, reward: 2000 };
  } else if (!claimedMilestones.includes('consistency-12-weeks')) {
    nextMilestone = { name: 'Tactical Athlete', weeksNeeded: 12, reward: 3000 };
  }

  const eligible = await isDualTrainingBonusEligible(userId);

  return {
    isLinked: true,
    linkBonus: {
      available: !status.link!.linkBonusClaimed,
      claimed: status.link!.linkBonusClaimed,
      amount: 500,
    },
    dualTraining: {
      eligible,
      multiplier: eligible ? 10 : 0,
      opmodeSessionsThisWeek: activity.currentWeekOpmodeCount,
      gunfighterSessionsThisWeek: activity.currentWeekGunfighterCount,
    },
    consistency: {
      consecutiveWeeks: activity.consecutiveDualWeeks,
      nextMilestone,
    },
  };
}
